import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, CreditCard, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { useCart } from "@/cart/CartContext";
import { buildSlots, fmtSlot } from "@/utils/slots";
import { PHONE_DISPLAY } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-white border-2 border-zinc-700 focus:border-ember focus:ring-1 focus:ring-ember outline-none px-4 py-4 text-base text-ink placeholder:text-zinc-500 transition-colors duration-300";

export default function CartPage() {
  const { lines, remove, setQty, total, count } = useCart();
  const [form, setForm] = useState({ name: "", phone: "", notes: "" });
  const [pickup, setPickup] = useState("ASAP");
  const [sending, setSending] = useState(false);
  const [prep, setPrep] = useState(20);
  const [accepting, setAccepting] = useState(true);
  const navigate = useNavigate();
  const slots = useMemo(() => buildSlots(prep), [prep]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => {
      setPrep(data.prep_minutes);
      setAccepting(data.accepting_orders);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data: order } = await axios.post(`${API}/orders`, {
        ...form,
        pickup_time: pickup,
        lines: lines.map((l) => ({ item_id: l.item_id, size: l.size, qty: l.qty, extras: l.extras.map((x) => x.id), choices: (l.choices || []).map((x) => x.id) })),
      });
      const { data: checkout } = await axios.post(`${API}/payments/checkout`, {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch {
      toast.error("Couldn't start payment", { description: `Please try again or call ${PHONE_DISPLAY}.` });
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 pb-20" data-testid="cart-page">
      <div className="max-w-3xl mx-auto px-6 pt-10">
        <Link to="/order" className="inline-flex items-center gap-2 border-2 border-zinc-700 bg-white hover:border-ember px-6 py-3.5 font-mono font-bold text-sm tracking-wide uppercase text-ink hover:text-ember transition-colors duration-300 mb-8" data-testid="cart-back-to-order">
          <ArrowLeft className="w-5 h-5" /> Add more items
        </Link>
        <h1 className="font-display text-6xl leading-none uppercase text-gold-shine mb-10" data-testid="cart-heading">Your Order</h1>

        {lines.length === 0 ? (
          <div className="border border-zinc-800 bg-white/40 p-10 text-center" data-testid="cart-empty">
            <ShoppingBag className="w-10 h-10 mx-auto text-zinc-500 mb-4" />
            <p className="text-zinc-500 mb-6">Your cart is empty.</p>
            <Link to="/order" className="inline-block bg-ember hover:bg-ember-hover text-white font-semibold px-8 py-3 text-sm tracking-wide uppercase transition-colors duration-300" data-testid="cart-empty-browse">
              Browse the menu
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-8" data-testid="cart-form">
            <div className="border border-zinc-800 bg-white/60" data-testid="cart-lines">
              {lines.map((l) => (
                <div key={l.key} className="flex items-center gap-4 px-5 py-4 border-b border-zinc-800/60 last:border-0" data-testid={`cart-line-${l.key}`}>
                  <div className="flex-1">
                    <p className="text-base text-ink font-semibold">
                      {l.name}{l.size ? ` (${l.size})` : ""}
                    </p>
                    {l.extras?.length > 0 && (
                      <p className="text-sm text-zinc-500 mt-0.5">+ {l.extras.map((e) => e.name).join(", ")}</p>
                    )}
                    {l.choices?.length > 0 && (
                      <p className="text-sm text-ember mt-0.5">— {l.choices.map((c) => c.name).join(", ")}</p>
                    )}
                    <p className="font-mono font-bold text-base text-ember mt-1">${(l.unit * l.qty).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center border border-zinc-800">
                    <button type="button" onClick={() => setQty(l.key, l.qty - 1)} className="px-2.5 py-2 text-zinc-500 hover:text-ember" aria-label="Less" data-testid={`cart-minus-${l.key}`}>
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-2 font-mono text-sm">{l.qty}</span>
                    <button type="button" onClick={() => setQty(l.key, l.qty + 1)} className="px-2.5 py-2 text-zinc-500 hover:text-ember" aria-label="More" data-testid={`cart-plus-${l.key}`}>
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <button type="button" onClick={() => remove(l.key)} aria-label="Remove" data-testid={`cart-remove-${l.key}`} className="text-zinc-500 hover:text-ember transition-colors duration-300">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex items-center justify-between px-5 py-4 bg-coal-800/60">
                <span className="font-mono text-sm font-bold tracking-[0.2em] uppercase text-zinc-500">Total · {count} item{count > 1 ? "s" : ""}</span>
                <span className="font-display text-4xl text-ember" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
            </div>

            <div data-testid="pickup-time-block">
              <p className="font-mono text-sm font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">Pickup time</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPickup("ASAP")}
                  data-testid="pickup-asap-button"
                  className={`px-4 py-4 border-2 font-mono text-sm font-bold tracking-[0.15em] uppercase transition-colors duration-300 ${
                    pickup === "ASAP" ? "border-ember bg-ember text-white" : "border-zinc-700 text-zinc-500 hover:border-ember hover:text-ember"
                  }`}
                >
                  ASAP — ready in ~{prep} min
                </button>
                <select
                  aria-label="Choose a pickup time"
                  data-testid="pickup-slot-select"
                  value={pickup === "ASAP" ? "" : pickup}
                  onChange={(e) => setPickup(e.target.value || "ASAP")}
                  className={`${inputCls} ${pickup !== "ASAP" ? "border-ember" : ""}`}
                >
                  <option value="">Choose a time…</option>
                  {slots.map((s) => (
                    <option key={s} value={s}>{fmtSlot(s)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cart-name" className="block font-mono text-sm font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">Name</label>
                <input id="cart-name" data-testid="cart-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
              </div>
              <div>
                <label htmlFor="cart-phone" className="block font-mono text-sm font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">Phone</label>
                <input id="cart-phone" data-testid="cart-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="04XX XXX XXX" className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="cart-notes" className="block font-mono text-sm font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">Notes <span className="text-zinc-600">(sauces, wing flavour, instructions…)</span></label>
              <p className="font-mono text-sm font-bold tracking-[0.1em] uppercase text-ember mb-2" data-testid="toppings-notes-hint">
                Ordered Extra Toppings on a pizza? List up to 2 toppings here
              </p>
              <input id="cart-notes" data-testid="cart-notes-input" value={form.notes} onChange={set("notes")} placeholder="e.g. extra pepperoni & mushroom, no onion…" className={inputCls} />
            </div>

            {!accepting && (
              <div className="border border-ember bg-ember/10 px-5 py-4" data-testid="ordering-paused-notice">
                <p className="font-mono text-xs tracking-[0.15em] uppercase text-ember">
                  Online ordering is paused right now — please call {PHONE_DISPLAY} to order
                </p>
              </div>
            )}
            <div className="border-2 border-ember bg-ember/10 px-5 py-4" data-testid="pickup-card-notice">
              <p className="font-mono text-sm font-bold tracking-[0.15em] uppercase text-ember">Pickup only · Card payment only</p>
              <p className="text-sm text-zinc-400 mt-1">
                Website orders are pickup from 126A Eighth St, Mildura and paid by card online. No cash, no delivery — for delivery order via Uber Eats, DoorDash or Menulog.
              </p>
            </div>
            <motion.button
              type="submit"
              disabled={sending || !accepting}
              whileTap={{ scale: 0.98 }}
              data-testid="cart-pay-button"
              className="w-full inline-flex items-center justify-center gap-3 bg-ember hover:bg-ember-hover disabled:opacity-50 text-white font-bold px-10 py-5 text-lg tracking-wide uppercase transition-colors duration-300 ember-glow"
            >
              <CreditCard className="w-4 h-4" />
              {sending ? "Opening secure payment…" : `Pay Online — $${total.toFixed(2)}`}
            </motion.button>
            <p className="font-mono text-xs text-zinc-500 text-center">Secure card payment · Pickup in store · We call if anything's unclear</p>
          </form>
        )}
      </div>
    </div>
  );
}
